import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Dialog, DialogContent, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Bed, User, Waves, ShowerHead } from 'lucide-react';

type ImageCarouselProps = {
  images: { url: string; alt: string }[];
};

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenIndex, setFullScreenIndex] = useState(0);

  return (
    <div className="bg-white">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-semibold mb-4">La Maison</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Carousel */}
        <div>
          <div className="w-full h-[45vh] lg:h-[500px] relative rounded-xl overflow-hidden shadow-lg group bg-gray-100">
            <Swiper
              spaceBetween={0}
              slidesPerView={1}
              pagination={{ clickable: true }}
              navigation={{ prevEl: '.swiper-maison-prev', nextEl: '.swiper-maison-next' }}
              modules={[Pagination, Navigation]}
              className="w-full h-full rounded-xl"
            >
              {images.map((image, index) => (
                <SwiperSlide key={index} className="w-full h-full">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover cursor-pointer"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    onClick={() => { setFullScreenIndex(index); setIsFullScreen(true); }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <button className="swiper-maison-prev flex absolute top-1/2 -translate-y-1/2 left-2 z-10 p-2 bg-white/70 hover:bg-white rounded-full shadow transition-all"><ChevronLeft className="h-5 w-5" /></button>
            <button className="swiper-maison-next flex absolute top-1/2 -translate-y-1/2 right-2 z-10 p-2 bg-white/70 hover:bg-white rounded-full shadow transition-all"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <p className="text-center text-xs text-gray-400 mt-2 lg:hidden">Appuyez sur une photo pour l'agrandir</p>
        </div>

        {/* Information Section */}
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-semibold mb-4">Une maison spacieuse et confortable</h3>
            <p className="text-gray-600 text-lg leading-relaxed">
              Ty Gwennili est une maison de vacances moderne située à 300 mètres de la plage. Elle offre un espace de vie lumineux et accueillant, avec 3 chambres pour 6 personnes, ainsi qu'un jardin clos.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Bed className="h-6 w-6 text-gray-600" />
              <span className="text-gray-800">3 Chambres</span>
            </div>
            <div className="flex items-center gap-3">
              <User className="h-6 w-6 text-gray-600" />
              <span className="text-gray-800">6 Voyageurs</span>
            </div>
            <div className="flex items-center gap-3">
              <ShowerHead className="h-6 w-6 text-gray-600" />
              <span className="text-gray-800">2 Salles de bain</span>
            </div>
            <div className="flex items-center gap-3">
              <Waves className="h-6 w-6 text-gray-600" />
              <span className="text-gray-800">Plage à 300m</span>
            </div>
          </div>
        </div>
      </div>

      {/* Plein écran */}
      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="p-0 max-w-full w-screen h-screen bg-black flex items-center justify-center">
          <DialogDescription className="sr-only">Image en plein écran</DialogDescription>
          <div className="relative w-full h-full">
            <Swiper
              spaceBetween={0}
              slidesPerView={1}
              initialSlide={fullScreenIndex}
              navigation={{ prevEl: '.swiper-maison-fs-prev', nextEl: '.swiper-maison-fs-next' }}
              modules={[Navigation]}
              className="w-full h-full"
            >
              {images.map((image, index) => (
                <SwiperSlide key={index} className="flex items-center justify-center">
                  <img src={image.url} alt={image.alt} className="max-w-full max-h-full object-contain" loading="lazy" decoding="async" />
                </SwiperSlide>
              ))}
            </Swiper>
            <button className="swiper-maison-fs-prev absolute top-1/2 -translate-y-1/2 left-4 z-10 p-3 bg-white/70 rounded-full"><ChevronLeft className="h-6 w-6" /></button>
            <button className="swiper-maison-fs-next absolute top-1/2 -translate-y-1/2 right-4 z-10 p-3 bg-white/70 rounded-full"><ChevronRight className="h-6 w-6" /></button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageCarousel;